import { StringObject } from "../../../objects/StringObject";
import { styleTxn } from "../../../utils/style-txn";
import {
  StringDevBadgeDescriptor,
  StringDevBadgeEntry,
  StringDevBadgeMeasurement,
  StringDevBadgeOverlayModule,
  StringDevManagedBadge,
} from "../core/StringDevBadgeOverlayModule";
import {
  OverlayBaseMetrics,
  StringDevOverlayBadgeState,
  resolveOverlayAnchor,
} from "../core/StringDevOverlayModule";
import { resolveDevtoolsIcon } from "../core/StringDevIconRegistry";
import { StringDevPersistedState } from "../core/StringDevPersistedState";
import { startPointerDrag } from "../../../utils/pointerDrag";
import { getStringDevViewportPolicy } from "../core/StringDevViewportPolicy";
import { StringDevtoolSubBadge } from "../../../models/devtools/StringDevtool";
import { INVIEW_CSS } from "./StringDevInview.css";

const ENTER_MARKER_SHIFT_X = 1;
const MARKER_VIEWPORT_EDGE_GAP = 4;
const MARKER_VIEWPORT_OVERFLOW_GUARD = 4;
const MARKER_FALLBACK_WIDTH = 64;
const MARKER_FALLBACK_HEIGHT = 16;
const MARKER_CONNECTOR_WIDTH = 14;

type MarkerKind = "enter" | "exit";

interface MarkerOffset {
  xPercent: number;
}

interface InviewExtra {
  outline: HTMLDivElement;
  enterConnector: HTMLDivElement;
  exitConnector: HTMLDivElement;
  enterMarker: HTMLDivElement;
  exitMarker: HTMLDivElement;
  enterMarkerLabel: HTMLSpanElement;
  exitMarkerLabel: HTMLSpanElement;
}

interface InviewMeasurement extends StringDevBadgeMeasurement {
  overlayVisible: boolean;
  badgeVisible: boolean;
  markersVisible: boolean;
  inview: boolean;
  outlineDocX: number;
  outlineDocY: number;
  outlineWidth: number;
  outlineHeight: number;
  enterConnectorDocX: number;
  enterConnectorDocY: number;
  enterConnectorWidth: number;
  enterConnectorHeight: number;
  exitConnectorDocX: number;
  exitConnectorDocY: number;
  exitConnectorWidth: number;
  exitConnectorHeight: number;
  enterMarkerDocX: number;
  enterMarkerDocY: number;
  enterMarkerFlippedY: boolean;
  exitMarkerDocX: number;
  exitMarkerDocY: number;
  exitMarkerFlippedY: boolean;
}

interface LiveStickyGeometry {
  contentX: number;
  contentY: number;
  width: number;
  height: number;
}

function fixedTranslate(x: number, y: number): string {
  return `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`;
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

const EMPTY_GEOMETRY = {
  outlineDocX: 0,
  outlineDocY: 0,
  outlineWidth: 0,
  outlineHeight: 0,
  enterConnectorDocX: 0,
  enterConnectorDocY: 0,
  enterConnectorWidth: 0,
  enterConnectorHeight: 0,
  exitConnectorDocX: 0,
  exitConnectorDocY: 0,
  exitConnectorWidth: 0,
  exitConnectorHeight: 0,
  enterMarkerDocX: 0,
  enterMarkerDocY: 0,
  enterMarkerFlippedY: false,
  exitMarkerDocX: 0,
  exitMarkerDocY: 0,
  exitMarkerFlippedY: false,
} as const;

export class StringDevInview extends StringDevBadgeOverlayModule<InviewExtra, InviewMeasurement> {
  static override devtool = {
    id: "inview",
    label: "Inview",
    icon: "intersection",
    order: 25,
    group: 2,
    hotkey: { key: "I", shiftKey: true },
    styles: INVIEW_CSS,
    overlay: {
      layerName: "devtools-inview",
      zIndex: 10011,
      layerAttribute: "data-string-inview-layer",
      overlayId: "inview",
      defaultEnabled: true,
    },
    connects: { global: true },
  };

  protected override get overlayBadgeCount(): number {
    return 2;
  }

  protected override getDevtoolSubBadges(): StringDevtoolSubBadge[] {
    return [
      {
        id: "hide-all-offsets",
        icon: "nooffset",
        label: "Hide all inview offsets",
        selectorAttribute: "data-string-inview-hide-all-offsets",
        onClick: () => this.disableAllOffsets(),
      },
    ];
  }

  private entryEnabled: Map<string, boolean> = new Map();
  private markerSizeCache = new WeakMap<HTMLElement, { width: number; height: number }>();

  private readonly disabledStore = new StringDevPersistedState<string[]>({
    moduleId: "inview",
    name: "disabled-targets",
    default: [],
    validate: (raw) =>
      Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : null,
    legacyKeys: ["string-devtools:inview"],
  });

  private readonly markerStore = new StringDevPersistedState<
    Record<string, Partial<Record<MarkerKind, MarkerOffset>>>
  >({
    moduleId: "inview",
    name: "marker-offsets",
    default: {},
    validate: (raw) => {
      if (!raw || typeof raw !== "object") return null;
      const out: Record<string, Partial<Record<MarkerKind, MarkerOffset>>> = {};
      for (const [targetId, value] of Object.entries(raw as Record<string, unknown>)) {
        if (!value || typeof value !== "object") continue;
        const next: Partial<Record<MarkerKind, MarkerOffset>> = {};
        const v = value as { enter?: unknown; exit?: unknown };
        if (v.enter && typeof (v.enter as MarkerOffset).xPercent === "number") {
          next.enter = { xPercent: (v.enter as MarkerOffset).xPercent };
        }
        if (v.exit && typeof (v.exit as MarkerOffset).xPercent === "number") {
          next.exit = { xPercent: (v.exit as MarkerOffset).xPercent };
        }
        if (next.enter || next.exit) out[targetId] = next;
      }
      return out;
    },
    legacyKeys: ["string-devtools:inview-marker-offsets"],
  });

  private disabledTargetIds: Set<string> = new Set(this.disabledStore.read());
  private markerOffsets: Map<string, Partial<Record<MarkerKind, MarkerOffset>>> = new Map(
    Object.entries(this.markerStore.read()),
  );
  private stableCoarseViewportWidth = 0;
  private stableCoarseViewportHeight = 0;

  protected getBadgeDescriptors(
    _object: StringObject,
    _targetId: string,
    _depth: number,
  ): Array<StringDevBadgeDescriptor> {
    return [
      {
        id: "inview-toggle",
        slot: "top-left",
        group: "main",
        selectorAttribute: "data-string-inview-toggle",
      },
      {
        id: "offsets",
        slot: "top-left",
        group: "main",
        selectorAttribute: "data-string-offsets-badge",
        attributes: {
          "data-string-inview-tool": "offsets",
        },
      },
    ];
  }

  protected createBadgeExtra(
    object: StringObject,
    _badge: HTMLButtonElement,
    targetId: string,
    _depth: number,
  ): InviewExtra {
    const savedEnabled = !this.disabledTargetIds.has(targetId);
    this.entryEnabled.set(object.id, savedEnabled);

    const outline = document.createElement("div");
    outline.setAttribute("data-string-offset-outline", "");
    outline.setAttribute("data-target-id", targetId);
    outline.setAttribute("data-visible", "false");

    const enterConnector = document.createElement("div");
    enterConnector.setAttribute("data-string-offset-connector", "enter");
    enterConnector.setAttribute("data-stdg", "");
    enterConnector.setAttribute("data-target-id", targetId);
    enterConnector.setAttribute("data-visible", "false");

    const exitConnector = document.createElement("div");
    exitConnector.setAttribute("data-string-offset-connector", "exit");
    exitConnector.setAttribute("data-stdg", "");
    exitConnector.setAttribute("data-target-id", targetId);
    exitConnector.setAttribute("data-visible", "false");

    const enterMarker = document.createElement("div");
    enterMarker.setAttribute("data-string-offset-edge-marker", "enter");
    enterMarker.setAttribute("data-stdg", "");
    enterMarker.setAttribute("data-target-id", targetId);
    enterMarker.setAttribute("data-visible", "false");
    const enterMarkerLabel = document.createElement("span");
    enterMarkerLabel.setAttribute("data-string-offset-edge-marker-label", "");
    enterMarker.append(enterMarkerLabel);

    const exitMarker = document.createElement("div");
    exitMarker.setAttribute("data-string-offset-edge-marker", "exit");
    exitMarker.setAttribute("data-stdg", "");
    exitMarker.setAttribute("data-target-id", targetId);
    exitMarker.setAttribute("data-visible", "false");
    const exitMarkerLabel = document.createElement("span");
    exitMarkerLabel.setAttribute("data-string-offset-edge-marker-label", "");
    exitMarker.append(exitMarkerLabel);

    const world = this.viewportLayer.getWorldElement(this.getWorldHost());
    world.appendChild(outline);
    world.appendChild(enterConnector);
    world.appendChild(exitConnector);
    world.appendChild(enterMarker);
    world.appendChild(exitMarker);
    const extra = {
      outline,
      enterConnector,
      exitConnector,
      enterMarker,
      exitMarker,
      enterMarkerLabel,
      exitMarkerLabel,
    };
    this.syncCompactModeForExtra(extra);
    return extra;
  }

  protected destroyBadgeExtra(entry: StringDevBadgeEntry<InviewExtra>): void {
    entry.extra.outline.remove();
    entry.extra.enterConnector.remove();
    entry.extra.exitConnector.remove();
    entry.extra.enterMarker.remove();
    entry.extra.exitMarker.remove();
    this.entryEnabled.delete(entry.object.id);
  }

  protected bindBadge(entry: StringDevBadgeEntry<InviewExtra>): Array<() => void> {
    const onInviewChange = () => {
      this.renderBadge(entry);
      this.scheduleSingleSync(entry);
    };

    const enterCleanup = this.bindMarkerDrag(entry, "enter", [
      entry.extra.enterMarker,
      entry.extra.enterConnector,
    ]);
    const exitCleanup = this.bindMarkerDrag(entry, "exit", [
      entry.extra.exitMarker,
      entry.extra.exitConnector,
    ]);

    this.events.on(entry.object.getScopedEventName("object:inview"), onInviewChange);
    return [
      enterCleanup,
      exitCleanup,
      () => this.events.off(entry.object.getScopedEventName("object:inview"), onInviewChange),
    ];
  }

  protected measureEntry(
    entry: StringDevBadgeEntry<InviewExtra>,
    metrics: OverlayBaseMetrics,
  ): InviewMeasurement {
    const badges = super.measureEntry(entry, metrics).badges;
    const object = entry.object;
    const entryEnabled = this.isEntryEnabled(object.id);
    const stickyGeometry = metrics.visible
      ? null
      : this.resolveLiveStickyGeometry(object.htmlElement);
    const geometryVisible = metrics.visible || stickyGeometry != null;

    // Resolve element geometry: from live metrics if in viewport, else from stored properties.
    const contentX = stickyGeometry
      ? stickyGeometry.contentX
      : metrics.visible
        ? metrics.contentX
        : (object.getProperty<number>("layout-doc-left") ?? 0);
    const contentY = stickyGeometry
      ? stickyGeometry.contentY
      : metrics.visible
        ? metrics.contentY
        : this.getObjectDocY(object);
    const width = stickyGeometry
      ? stickyGeometry.width
      : metrics.visible
        ? metrics.width
        : (object.getProperty<number>("layout-width") ??
          (object.getProperty<number>("half-width") ?? 0) * 2);
    const height = stickyGeometry
      ? stickyGeometry.height
      : metrics.visible
        ? metrics.height
        : (object.getProperty<number>("layout-height") ??
          (object.getProperty<number>("half-height") ?? 0) * 2);

    if (width <= 0 || height <= 0) {
      return this.hiddenMeasurement(object, badges);
    }

    const scrollTop = this.getViewportScrollTop();
    const viewportHeight = this.getStableViewportHeight();

    const startViewport = object.getProperty<string>("enter-vp") ?? "bottom";
    const endViewport = object.getProperty<string>("exit-vp") ?? "top";
    const startElement = object.getProperty<string>("enter-el") ?? "top";
    const endElement = object.getProperty<string>("exit-el") ?? "bottom";
    const inviewStartPosition = object.getProperty<number>("inview-start-position");
    const inviewEndPosition = object.getProperty<number>("inview-end-position");

    const topAnchor = startElement === "bottom" ? contentY + height : contentY;
    const bottomAnchor = endElement === "top" ? contentY : contentY + height;
    const startVpOffset = resolveOverlayAnchor(startViewport) === "end" ? viewportHeight : 0;
    const endVpOffset = resolveOverlayAnchor(endViewport) === "end" ? viewportHeight : 0;
    const enterTrigger =
      inviewStartPosition != null ? inviewStartPosition + startVpOffset : topAnchor;
    const exitTrigger = inviewEndPosition != null ? inviewEndPosition + endVpOffset : bottomAnchor;

    const enterMarkerSize = this.measureMarkerSize(entry.extra.enterMarker);
    const exitMarkerSize = this.measureMarkerSize(entry.extra.exitMarker);
    const enterMarkerWidth = enterMarkerSize.width;
    const enterMarkerHeight = enterMarkerSize.height;
    const exitMarkerWidth = exitMarkerSize.width;
    const exitMarkerHeight = exitMarkerSize.height;
    const enterMarkerOverride = this.getMarkerOffset(entry.targetId, "enter");
    const exitMarkerOverride = this.getMarkerOffset(entry.targetId, "exit");
    const enterAnchorXPercent = this.clampMarkerPercentInViewport(
      contentX,
      width,
      enterMarkerOverride?.xPercent ?? 100,
      "enter",
      enterMarkerWidth,
    );
    const exitAnchorXPercent = this.clampMarkerPercentInViewport(
      contentX,
      width,
      exitMarkerOverride?.xPercent ?? 0,
      "exit",
      exitMarkerWidth,
    );
    const enterAnchorDocX = contentX + width * (enterAnchorXPercent / 100);
    const exitAnchorDocX = contentX + width * (exitAnchorXPercent / 100);
    const enterConnectorDocX = enterAnchorDocX + ENTER_MARKER_SHIFT_X;
    const enterConnectorDocY = Math.min(topAnchor, enterTrigger);
    const enterConnectorWidth = MARKER_CONNECTOR_WIDTH;
    const enterConnectorHeight = Math.abs(enterTrigger - topAnchor);
    const exitConnectorDocX = exitAnchorDocX;
    const exitConnectorDocY = Math.min(bottomAnchor, exitTrigger);
    const exitConnectorWidth = MARKER_CONNECTOR_WIDTH;
    const exitConnectorHeight = Math.abs(exitTrigger - bottomAnchor);
    const enterMarkerDocX = enterAnchorDocX + ENTER_MARKER_SHIFT_X;
    const exitMarkerDocX = exitAnchorDocX;
    const documentBottom =
      this.data.viewport.contentHeight || this.data.viewport.windowHeight || window.innerHeight;
    const enterMarkerPastTop = enterTrigger - enterMarkerHeight < 0;
    const exitMarkerPastTop = exitTrigger < 0;
    const enterMarkerPastBottom = enterTrigger > documentBottom;
    const exitMarkerPastBottom = exitTrigger + exitMarkerHeight > documentBottom;
    const enterMarkerFlippedY = enterMarkerPastTop || enterMarkerPastBottom;
    const exitMarkerFlippedY = exitMarkerPastTop || exitMarkerPastBottom;
    const enterMarkerDocY = enterMarkerPastBottom
      ? documentBottom
      : enterTrigger + (enterMarkerPastTop ? enterMarkerHeight : 0);
    const exitMarkerDocY = exitMarkerPastBottom
      ? documentBottom - exitMarkerHeight
      : exitTrigger + (exitMarkerPastTop ? exitMarkerHeight : 0);

    // Each trigger line is visible if it falls within (or just above) the viewport.
    const enterInView =
      enterTrigger - scrollTop >= -enterMarkerHeight && enterTrigger - scrollTop <= viewportHeight;
    const exitInView =
      exitTrigger - scrollTop >= -exitMarkerHeight && exitTrigger - scrollTop <= viewportHeight;
    const markersVisible = this.enabled && entryEnabled && (enterInView || exitInView);

    // Nothing to show at all.
    if (!geometryVisible && !markersVisible) {
      return this.hiddenMeasurement(object, badges);
    }

    return {
      visible: geometryVisible,
      badges,
      overlayVisible: this.enabled && entryEnabled,
      badgeVisible: this.enabled,
      markersVisible,
      inview: object.getProperty<boolean>("is-inview") === true,
      outlineDocX: contentX,
      outlineDocY: contentY,
      outlineWidth: width,
      outlineHeight: height,
      enterConnectorDocX,
      enterConnectorDocY,
      enterConnectorWidth,
      enterConnectorHeight,
      exitConnectorDocX,
      exitConnectorDocY,
      exitConnectorWidth,
      exitConnectorHeight,
      enterMarkerDocX,
      enterMarkerDocY,
      enterMarkerFlippedY,
      exitMarkerDocX,
      exitMarkerDocY,
      exitMarkerFlippedY,
    };
  }

  protected afterBadgeMeasurement(
    entry: StringDevBadgeEntry<InviewExtra>,
    measurement: InviewMeasurement | undefined,
  ): void {
    this.syncCompactMode(entry);
    const object = entry.object;
    const startViewport = object.getProperty<string>("enter-vp") ?? "bottom";
    const endViewport = object.getProperty<string>("exit-vp") ?? "top";
    this.syncMarkerContent(entry, "enter", this.getViewportMarkerLabel(startViewport));
    this.syncMarkerContent(entry, "exit", this.getViewportMarkerLabel(endViewport));
    const { outline, enterConnector, exitConnector, enterMarker, exitMarker } = entry.extra;

    const outlineVisible = !!measurement?.visible && !!measurement.overlayVisible;
    const markersVisible = !!measurement?.markersVisible && !!measurement.overlayVisible;

    outline.setAttribute("data-visible", outlineVisible ? "true" : "false");
    const m = measurement;
    if (m && outlineVisible) {
      outline.setAttribute("data-inview", m.inview ? "true" : "false");
      styleTxn.setProps(outline, {
        transform: fixedTranslate(m.outlineDocX, m.outlineDocY),
        width: `${Math.max(0, m.outlineWidth)}px`,
        height: `${Math.max(0, m.outlineHeight)}px`,
      });
    }

    if (!markersVisible) {
      for (const part of [enterConnector, exitConnector, enterMarker, exitMarker]) {
        part.setAttribute("data-visible", "false");
      }
      return;
    }
    if (!m) return;

    const inviewAttr = m.inview ? "true" : "false";
    for (const part of [enterConnector, exitConnector, enterMarker, exitMarker]) {
      part.setAttribute("data-visible", "true");
      part.setAttribute("data-inview", inviewAttr);
    }
    enterMarker.setAttribute("data-flip-y", m.enterMarkerFlippedY ? "true" : "false");
    exitMarker.setAttribute("data-flip-y", m.exitMarkerFlippedY ? "true" : "false");
    styleTxn.setProps(enterConnector, {
      transform: fixedTranslate(
        m.enterConnectorDocX - m.enterConnectorWidth / 2,
        m.enterConnectorDocY,
      ),
      width: `${Math.max(0, m.enterConnectorWidth)}px`,
      height: `${Math.max(0, m.enterConnectorHeight)}px`,
    });
    styleTxn.setProps(exitConnector, {
      transform: fixedTranslate(
        m.exitConnectorDocX - m.exitConnectorWidth / 2,
        m.exitConnectorDocY,
      ),
      width: `${Math.max(0, m.exitConnectorWidth)}px`,
      height: `${Math.max(0, m.exitConnectorHeight)}px`,
    });
    styleTxn.setProps(enterMarker, {
      transform: `${fixedTranslate(m.enterMarkerDocX, m.enterMarkerDocY)} translate(-100%, -100%)`,
    });
    styleTxn.setProps(exitMarker, {
      transform: fixedTranslate(m.exitMarkerDocX, m.exitMarkerDocY),
    });
  }

  protected onBadgeClick(
    entry: StringDevBadgeEntry<InviewExtra>,
    _event: MouseEvent,
    badge: StringDevManagedBadge,
  ): void {
    if (badge.id === "inview-toggle") {
      this.toggleInview(entry.object);
      this.renderBadge(entry);
      this.scheduleSingleSync(entry);
      return;
    }

    if (badge.id === "offsets") {
      this.setEntryEnabled(entry.object.id, !this.isEntryEnabled(entry.object.id));
      this.renderBadge(entry);
      this.scheduleFullSync();
    }
  }

  protected getBadgeState(
    entry: StringDevBadgeEntry<InviewExtra>,
    badge?: StringDevManagedBadge,
  ): StringDevOverlayBadgeState {
    if (badge?.id === "offsets") {
      const isActive = this.isEntryEnabled(entry.object.id);
      return {
        active: isActive,
        title: isActive ? "Hide offsets for this element" : "Show offsets for this element",
        html: resolveDevtoolsIcon(16, "offset"),
      };
    }

    const state = this.getInviewBadgeState(entry.object);
    return {
      active: state.active,
      title: state.title,
      html: state.html,
      attributes: {
        "data-inview": state.inview,
        "data-blocked": state.blocked,
      },
    };
  }

  private hiddenMeasurement(
    object: StringObject,
    badges: StringDevBadgeMeasurement["badges"],
  ): InviewMeasurement {
    const hiddenBadges: StringDevBadgeMeasurement["badges"] = {};
    for (const [id, measurement] of Object.entries(badges)) {
      hiddenBadges[id] = { ...measurement, visible: false };
    }

    return {
      visible: false,
      badges: hiddenBadges,
      overlayVisible: this.enabled && this.isEntryEnabled(object.id),
      badgeVisible: this.enabled,
      markersVisible: false,
      inview: object.getProperty<boolean>("is-inview") === true,
      ...EMPTY_GEOMETRY,
    };
  }

  private getInviewBadgeState(object: StringObject): {
    active: boolean;
    blocked: boolean;
    inview: boolean;
    title: string;
    html: string;
  } {
    const isInView = object.getProperty<boolean>("is-inview") === true;
    const isBlocked = object.isInviewAutoBlocked();

    return {
      active: this.isInviewBadgeActive(object),
      blocked: isBlocked,
      inview: isInView,
      title: isBlocked ? "Enable automatic -inview" : "Disable automatic -inview",
      html: resolveDevtoolsIcon(16, "eye"),
    };
  }

  private toggleInview(object: StringObject): void {
    if (this.isInviewBadgeActive(object)) {
      object.setInviewManualActive(false);
      object.setInviewAutoBlocked(true);
      object.htmlElement.classList.remove("-inview");
      return;
    }

    object.setInviewAutoBlocked(false);
    object.setInviewManualActive(true);
    object.syncInviewClass();
  }

  private isInviewBadgeActive(object: StringObject): boolean {
    if (object.isInviewAutoBlocked()) return false;
    if (object.isInviewManualActive()) return true;
    if (object.getProperty<boolean>("is-inview") === true) return true;
    if (object.getProperty<boolean>("repeat") === true) return false;
    return object.htmlElement.classList.contains("-inview");
  }

  private isEntryEnabled(objectId: string): boolean {
    return this.entryEnabled.get(objectId) !== false;
  }

  private setEntryEnabled(objectId: string, enabled: boolean): void {
    this.entryEnabled.set(objectId, enabled);
    const entry = this.entries.get(objectId);
    if (entry) {
      const targetId = entry.targetId;
      if (enabled) {
        this.disabledTargetIds.delete(targetId);
      } else {
        this.disabledTargetIds.add(targetId);
      }
      this.disabledStore.write([...this.disabledTargetIds]);
    }
    if (!this.enabled) return;
    if (entry) this.scheduleSingleSync(entry);
    else this.scheduleSync();
  }

  private disableAllOffsets(): void {
    let changed = false;
    for (const entry of this.entries.values()) {
      if (this.entryEnabled.get(entry.object.id) === false) continue;
      this.entryEnabled.set(entry.object.id, false);
      this.disabledTargetIds.add(entry.targetId);
      this.renderBadge(entry);
      changed = true;
    }

    if (!changed) return;
    this.disabledStore.write([...this.disabledTargetIds]);
    if (this.enabled) this.scheduleSync();
  }

  private bindMarkerDrag(
    entry: StringDevBadgeEntry<InviewExtra>,
    kind: MarkerKind,
    dragHandles: HTMLDivElement[],
  ): () => void {
    const onPointerDown = (event: PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const element = entry.object.htmlElement;
      if (!element?.isConnected) return;

      const elementRect = element.getBoundingClientRect();
      if (elementRect.width <= 0) return;

      const markerWidth =
        kind === "enter"
          ? entry.extra.enterMarker.getBoundingClientRect().width
          : entry.extra.exitMarker.getBoundingClientRect().width;
      const currentOffset = this.getMarkerOffset(entry.targetId, kind);
      const currentXPercent = this.clampMarkerPercentInViewport(
        elementRect.left + this.getViewportScrollLeft(),
        elementRect.width,
        currentOffset?.xPercent ?? (kind === "enter" ? 100 : 0),
        kind,
        markerWidth,
      );
      const anchorClientX = elementRect.left + elementRect.width * (currentXPercent / 100);
      const pointerOffsetX = event.clientX - anchorClientX;

      const badgeWorld = this.badgeLayer.getWorldElement(this.getWorldHost());
      const activeBadges = Array.from(
        badgeWorld.querySelectorAll<HTMLElement>(
          `[data-stdg-badge][data-target-id="${entry.targetId}"]`,
        ),
      );
      for (const el of activeBadges) {
        el.setAttribute("data-dragging", "");
      }
      badgeWorld.setAttribute("data-dragging-active", "");

      startPointerDrag(event, {
        onMove: (moveEvent) => {
          const liveElementRect = element.getBoundingClientRect();
          if (liveElementRect.width <= 0) return;
          const nextAnchorClientX = moveEvent.clientX - pointerOffsetX;
          const nextXPercent = this.clampMarkerPercentInClientViewport(
            liveElementRect,
            nextAnchorClientX,
            kind,
            markerWidth,
          );
          this.setMarkerOffset(entry.targetId, kind, { xPercent: nextXPercent });
          this.scheduleSingleSync(entry);
        },
        onEnd: () => {
          for (const el of activeBadges) {
            el.removeAttribute("data-dragging");
          }
          badgeWorld.removeAttribute("data-dragging-active");
        },
      });
    };

    for (const handle of dragHandles) {
      handle.addEventListener("pointerdown", onPointerDown);
    }

    return () => {
      for (const handle of dragHandles) {
        handle.removeEventListener("pointerdown", onPointerDown);
      }
    };
  }

  private getMarkerOffset(targetId: string, kind: MarkerKind): MarkerOffset | null {
    return this.markerOffsets.get(targetId)?.[kind] ?? null;
  }

  private setMarkerOffset(targetId: string, kind: MarkerKind, offset: MarkerOffset): void {
    const existing = this.markerOffsets.get(targetId) ?? {};
    existing[kind] = offset;
    this.markerOffsets.set(targetId, existing);
    this.markerStore.write(Object.fromEntries(this.markerOffsets.entries()));
  }

  private syncMarkerContent(
    entry: StringDevBadgeEntry<InviewExtra>,
    kind: MarkerKind,
    viewportLabel: "top" | "bottom",
  ): void {
    const marker = kind === "enter" ? entry.extra.enterMarker : entry.extra.exitMarker;
    const labelNode = kind === "enter" ? entry.extra.enterMarkerLabel : entry.extra.exitMarkerLabel;
    const label = this.getOverlayTargetLabel(entry.object);

    if (labelNode.textContent !== label) {
      labelNode.textContent = label;
    }
    this.setAttributeIfChanged(marker, "data-viewport-anchor", viewportLabel);
    const title = `${label} -> viewport ${viewportLabel}`;
    if (marker.title !== title) {
      marker.title = title;
    }
  }

  private getViewportMarkerLabel(raw: string | null | undefined): "top" | "bottom" {
    return resolveOverlayAnchor(raw) === "end" ? "bottom" : "top";
  }

  private measureMarkerSize(marker: HTMLElement): { width: number; height: number } {
    const rect = marker.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const size = { width: rect.width, height: rect.height };
      this.markerSizeCache.set(marker, size);
      return size;
    }

    return (
      this.markerSizeCache.get(marker) ?? {
        width: MARKER_FALLBACK_WIDTH,
        height: MARKER_FALLBACK_HEIGHT,
      }
    );
  }

  private resolveLiveStickyGeometry(element: HTMLElement): LiveStickyGeometry | null {
    if (!element?.isConnected) return null;
    if (window.getComputedStyle(element).position !== "sticky") return null;

    const rect = element.getBoundingClientRect();
    const liveMetrics = this.resolveLiveElementMetrics(element, rect);
    const width = liveMetrics.width;
    const height = liveMetrics.height;

    if (width <= 0 || height <= 0) return null;

    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const viewportHeight = this.data.viewport.windowHeight || window.innerHeight;
    const isVisible =
      rect.bottom >= 0 &&
      rect.top <= viewportHeight &&
      rect.right >= 0 &&
      rect.left <= viewportWidth;

    if (!isVisible || liveMetrics.docLeft == null || liveMetrics.docTop == null) return null;

    return {
      contentX: liveMetrics.docLeft,
      contentY: liveMetrics.docTop,
      width,
      height,
    };
  }

  private clampMarkerPercentInViewport(
    elementDocLeft: number,
    elementWidth: number,
    percent: number,
    kind: MarkerKind,
    markerWidth: number,
  ): number {
    if (elementWidth <= 0) {
      return kind === "enter" ? 100 : 0;
    }

    const desiredAnchorDocX = elementDocLeft + elementWidth * (clampPercent(percent) / 100);
    const scrollLeft = this.getViewportScrollLeft();
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const edgeInset = MARKER_VIEWPORT_EDGE_GAP + MARKER_VIEWPORT_OVERFLOW_GUARD;
    const viewportMinX = scrollLeft + edgeInset;
    const viewportMaxX = scrollLeft + viewportWidth - edgeInset;
    const connectorHalfWidth = MARKER_CONNECTOR_WIDTH / 2;
    const minAnchorDocX =
      kind === "enter"
        ? Math.max(
            viewportMinX + markerWidth - ENTER_MARKER_SHIFT_X,
            viewportMinX + connectorHalfWidth - ENTER_MARKER_SHIFT_X,
          )
        : viewportMinX + connectorHalfWidth;
    const maxAnchorDocX =
      kind === "enter"
        ? viewportMaxX - connectorHalfWidth - ENTER_MARKER_SHIFT_X
        : Math.min(viewportMaxX - markerWidth, viewportMaxX - connectorHalfWidth);
    const allowedMin = Math.max(elementDocLeft, minAnchorDocX);
    const allowedMax = Math.min(elementDocLeft + elementWidth, maxAnchorDocX);
    const anchorDocX = clamp(desiredAnchorDocX, allowedMin, Math.max(allowedMin, allowedMax));
    return clampPercent(((anchorDocX - elementDocLeft) / elementWidth) * 100);
  }

  private clampMarkerPercentInClientViewport(
    elementRect: DOMRect,
    anchorClientX: number,
    kind: MarkerKind,
    markerWidth: number,
  ): number {
    if (elementRect.width <= 0) {
      return kind === "enter" ? 100 : 0;
    }

    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const edgeInset = MARKER_VIEWPORT_EDGE_GAP + MARKER_VIEWPORT_OVERFLOW_GUARD;
    const connectorHalfWidth = MARKER_CONNECTOR_WIDTH / 2;
    const minAnchorClientX =
      kind === "enter"
        ? Math.max(
            edgeInset + markerWidth - ENTER_MARKER_SHIFT_X,
            edgeInset + connectorHalfWidth - ENTER_MARKER_SHIFT_X,
          )
        : edgeInset + connectorHalfWidth;
    const maxAnchorClientX =
      kind === "enter"
        ? viewportWidth - edgeInset - connectorHalfWidth - ENTER_MARKER_SHIFT_X
        : Math.min(
            viewportWidth - edgeInset - markerWidth,
            viewportWidth - edgeInset - connectorHalfWidth,
          );
    const allowedMin = Math.max(elementRect.left, minAnchorClientX);
    const allowedMax = Math.min(elementRect.right, maxAnchorClientX);
    const clampedAnchorClientX = clamp(anchorClientX, allowedMin, Math.max(allowedMin, allowedMax));

    return clampPercent(((clampedAnchorClientX - elementRect.left) / elementRect.width) * 100);
  }

  private syncCompactMode(entry: StringDevBadgeEntry<InviewExtra>): void {
    this.syncCompactModeForExtra(entry.extra);
  }

  private syncCompactModeForExtra(extra: InviewExtra): void {
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const policy = getStringDevViewportPolicy(viewportWidth);
    const compactValue = policy.compact ? "true" : "false";
    const pointerValue = policy.coarsePointer ? "coarse" : "fine";

    for (const element of [
      extra.outline,
      extra.enterConnector,
      extra.exitConnector,
      extra.enterMarker,
      extra.exitMarker,
    ]) {
      this.setAttributeIfChanged(element, "data-compact", compactValue);
      this.setAttributeIfChanged(element, "data-pointer", pointerValue);
    }
  }

  private setAttributeIfChanged(element: HTMLElement, name: string, value: string): void {
    if (element.getAttribute(name) !== value) {
      element.setAttribute(name, value);
    }
  }

  private getStableViewportHeight(): number {
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const viewportHeight = this.data.viewport.windowHeight || window.innerHeight;
    const policy = getStringDevViewportPolicy(viewportWidth);

    if (!policy.coarsePointer) {
      this.stableCoarseViewportWidth = viewportWidth;
      this.stableCoarseViewportHeight = viewportHeight;
      return viewportHeight;
    }

    if (Math.abs(this.stableCoarseViewportWidth - viewportWidth) > 1) {
      this.stableCoarseViewportWidth = viewportWidth;
      this.stableCoarseViewportHeight = viewportHeight;
      return viewportHeight;
    }

    this.stableCoarseViewportWidth = viewportWidth;
    this.stableCoarseViewportHeight = Math.max(this.stableCoarseViewportHeight, viewportHeight);
    return this.stableCoarseViewportHeight;
  }
}

export { StringDevInview as StringInview };
